import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { connect } from "react-redux";
import OuterContainer from "../components/layout/OuterContainer";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";
import NavBar from "../components/layout/NavBar";
import Loader from "../components/Loading";
import Button from "../components/common/Button";
import UserProfileImage from "../components/UserProfileImage";
import colors from "../utils/constants/colors";

// Styled Components
const Sidebar = styled.div`
  width: 350px;
  max-width: 30%;
  overflow-y: auto;
  padding: 15px;
`;

const TaskCard = styled.div`
  border: 1px solid ${(props) => (props.selected ? colors.primary : "#cfcfcf")};
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;

  &:hover {
    background-color: #f9f9f9;
  }
`;

const TaskInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  color: ${colors.primary};
`;

const Title = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.selected ? colors.primary : "#6f6f6f")};
`;

const PriceTag = styled.span`
  font-weight: bold;
  font-size: 16px;
  color: #6f6f6f;
`;

const TaskDetails = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  background-color: #ffffff;
  border-left: 1px solid #cfcfcf;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  color: #333;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-weight: bold;
  margin-bottom: 4px;
  display: block;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${colors.greyLight};
  border-radius: 6px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid ${colors.greyLight};
  border-radius: 6px;
  resize: none;
  height: 100px;
`;

const SubmitButton = styled(Button)`
  width: 100%;
`;

const NestedReply = ({ replies }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <div>
      <Button onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed ? "Show Replies" : "Hide Replies"}
      </Button>
      {!isCollapsed &&
        replies.map((reply) => (
          <div key={reply.id} style={{ marginLeft: "20px" }}>
            <p>
              <strong>{reply.user_name}:</strong> {reply.reply_text}
            </p>
          </div>
        ))}
    </div>
  );
};

const ManageTasks = (props) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    budget: "",
  });

  // Validate if token exists
  useEffect(() => {
    if (!props.state.user || !props.state.user.token) {
      console.error("No token found. Redirecting to login...");
      navigate("/login");
      return;
    }
  }, [props.state.user, navigate]);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await request(
          "POST",
          API_CONSTANT_MAP.fetch_user_tasks,
          null,
          { Authorization: `Bearer ${props.state.user.token}` }
        );
        if (response.data.success) {
          setTasks(response.data.tasks);
          if (response.data.tasks.length > 0) {
            setSelectedTask(response.data.tasks[0]); // Select first task by default
          }
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (props.state.user && props.state.user.token) {
      fetchTasks();
    }
  }, [props.state.user]);

  useEffect(() => {
    if (selectedTask) {
      setFormData({
        title: selectedTask.title,
        description: selectedTask.description,
        budget: selectedTask.budget,
      });
    }
  }, [selectedTask]);

  const handleTaskClick = (task) => setSelectedTask(task);

  const handleFormFieldChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleTaskUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, task_id: selectedTask.id };
      const response = await request(
        "POST",
        API_CONSTANT_MAP.update_task,
        payload,
        { Authorization: `Bearer ${props.state.user.token}` }
      );
      if (response.data.success) {
        alert("Task updated successfully!");
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.id === selectedTask.id ? { ...task, ...formData } : task
          )
        );
      } else {
        alert("Failed to update task.");
      }
    } catch (error) {
      console.error("Error updating task:", error);
    }
  };

  if (isLoading) return <Loader animating={true} />;

  if (!props.state.user || !props.state.user.token) {
    return <p>Unauthorized. Please log in.</p>;
  }

  return (
    <>
      <NavBar />
      <OuterContainer>
        <Sidebar>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              selected={selectedTask && task.id === selectedTask.id}
              onClick={() => handleTaskClick(task)}
            >
              <TaskInfo>
                <Title selected={selectedTask && task.id === selectedTask.id} style={{ textTransform: "capitalize" }}>
                  {task.title}
                </Title>
                <PriceTag>${task.budget}</PriceTag>
              </TaskInfo>
            </TaskCard>
          ))}
        </Sidebar>
        <TaskDetails>
          {selectedTask ? (
            <>
              <form onSubmit={handleTaskUpdate}>
                <SectionTitle>Edit Task</SectionTitle>
                <FormGroup>
                  <Label>Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={handleFormFieldChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    id="description"
                    value={formData.description}
                    onChange={handleFormFieldChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Budget</Label>
                  <Input
                    id="budget"
                    type="number"
                    value={formData.budget}
                    onChange={handleFormFieldChange}
                  />
                </FormGroup>
                <SubmitButton type="submit">Update Task</SubmitButton>
              </form>
              <SectionTitle>Offers</SectionTitle>
              {selectedTask.offers.map((offer) => (
                <p key={offer.id}>
                  <strong>${offer.offer_amount}</strong> by {offer.user_name}
                </p>
              ))}
              <SectionTitle>Questions</SectionTitle>
              {selectedTask.questions.map((question) => (
                <div key={question.id}>
                  <p>
                    <strong>{question.user_name}:</strong> {question.text}
                  </p>
                  <NestedReply replies={question.replies} />
                </div>
              ))}
            </>
          ) : (
            <p>Please select a task to view and edit its details.</p>
          )}
        </TaskDetails>
      </OuterContainer>
    </>
  );
};

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(ManageTasks);
